////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// ft3d/ft3d-bg-manifest.ts — FT3D parallax background manifest + feature flag.
// ----------------------------------------------------------------------------------------------------
// Phase 0 of tools/xz-parallax/FT3D_PARALLAX_BACKGROUND_PLAN.md: forest only, placeholder
// procedural-SVG silhouettes (data: URIs — no CDN asset, no art spend). Phase 1 grows the
// environment matrix; Phase 2 moves `sky` out of the env set into a weather×day/night
// grid (§4.2 of the plan); Phase 4 swaps data: URIs for cdn.fatetide.com/images/xz-bg/.
//
// SIZING IS IN WORLD FEET (heightFt / bandFt), converted to px via calcs.tickSpacing in
// FT3DParallaxBackground.layout(). Attempt-1 lesson: the canvas above-ground band can be
// 1000 ft / 16000+ px tall — any "fraction of the band" sizing turns one tile into a
// screen-filling blob. Trees are 20–90 ft tall no matter how tall the sky is.
//
// FALLBACK CONTRACT (plan §6): any env with no entry here renders NOTHING new — the map
// stays exactly the blank/gradient background it has today. Never degrade below that.
////////////////////////////////////////////////////////////////////////////////////////////////////////////////

import type { FT3DSvgComposer } from './ft3d-backdrop-kits';
import {
    forestFarSvg,
    forestNearSvg,
    // Cosmic FAR is now the AI far.webp band (cosmicSet); the SVG far composers (cosmicFarSvg/
    // wispBand/speckleBand/nebulaGlowBand/starClusterBand) stay exported from the kit as fallback
    // scaffolding but are no longer wired here. NEAR silhouettes remain the seeded SVGs below.
    rubbleNearSvg,
    coralNearSvg,
    kelpNearSvg,
    boundSeed,
} from './ft3d-backdrop-kits';

/** Props shared by every backdrop layer regardless of render mode. */
export interface FT3DBgBase {
    /** Tile height in world feet, bottom-anchored on the groundline. */
    heightFt?: number;
    /** Fraction of world X-pan this layer APPEARS to move at. Ground/tokens = 1.0; far ~0.15. */
    parallaxX: number;
    /**
     * Z drift factor. KEEP 0 for groundline-anchored silhouettes: scrollTop spans the whole
     * 16k-px canvas, so any nonzero factor sinks/floats the treeline by whole tree-heights.
     */
    parallaxY: number;
    blurPx?: number;
    opacity?: number;
    brightness?: number;
    saturate?: number;
    /**
     * FULL-BLEED VOID (2026-07-07): when true this layer fills the ENTIRE canvas (top → totalHeight,
     * NOT clamped to the groundline) and tiles on BOTH axes, so open-void environments (outer_space,
     * astral) render space above AND below the X axis with no horizon/ground break. The renderer also
     * flags the canvas so the .ground-layer goes transparent under it. Only meaningful on kind:'image'
     * (a seamless-XY `void.webp`); heightFt sizes the repeating tile in world-feet.
     */
    fillCanvas?: boolean;
}

/**
 * One backdrop layer — a TAGGED UNION on `kind`, one branch per mutually-exclusive render
 * mode. FT3DParallaxBackground renders via an explicit `switch (def.kind)`, so precedence is
 * a property of the data (each layer IS exactly one mode), not of a reader-side if-chain.
 */
export type FT3DBgLayer =
    | (FT3DBgBase & {
        kind: 'gradient';
        /** Gradient image for the sky band (sized/anchored to the groundline in layout()). */
        gradient: string;
        /** Flat color painted above the sky gradient band (upper sky, revealed by scrolling up). */
        skyUpper?: string;
        /** Sky gradient band height in world feet (groundline up). */
        bandFt?: number;
    })
    | (FT3DBgBase & {
        kind: 'image';
        /** Image url, seamless-tileable on X (Phase 0: inline data: SVG; Phase 4: CDN webp). */
        url: string;
    })
    | (FT3DBgBase & {
        kind: 'pool';
        /**
         * "Rooms don't repeat" seed pool (INDOOR_RENDER_PLAN.md §5): N distinct tiles for this
         * slot; FT3DParallaxBackground picks ONE per encounter via pickFromPool(pool, seedKey) — a
         * stable hash, NOT random, so the same room always renders the same tile across
         * reloads/players while different rooms of the same env/variant differ.
         */
        urlPool: string[];
    })
    | (FT3DBgBase & {
        kind: 'svg';
        /**
         * Seeded procedural COMPOSER (PROCEDURAL_BACKDROP_PLAN.md §1) — composes this band's tile
         * from the theme's element kit (ft3d-backdrop-kits.ts) with the per-encounter seed, so every
         * encounter of the same theme renders a DISTINCT but on-theme scene. FT3DParallaxBackground
         * derives the seed from encounter_backdrop_seed (GM re-roll) falling back to the
         * encounter id (§2).
         */
        svgFn: FT3DSvgComposer;
    });

export interface FT3DBgEnvSet {
    /** Day sky (one per env until the Phase 2 weather×day/night grid). */
    sky?: FT3DBgLayer;
    far?: FT3DBgLayer;
    near?: FT3DBgLayer;
    /**
     * Baked NIGHT overrides (ENV_MATRIX_PLAN.md §3/§9): distinct moonlit-ambient tiles + a night
     * sky gradient, swapped in by FT3DParallaxBackground when the campaign SunState classifies to
     * night. Any missing member falls back to its day counterpart (fallback contract — never
     * worse than today). Night tiles are authored readable at brightness(1.0); the encounter_light
     * vision mask composes ON TOP and is untouched by this swap (§3a).
     */
    skyNight?: FT3DBgLayer;
    farNight?: FT3DBgLayer;
    nearNight?: FT3DBgLayer;
    /**
     * FT3D horizon ring only (2026-09-27): a settlement VIGNETTE strip — the town dense in the middle, thinning to
     * fields at both ends on one grounded line — shown once per strip in the urban mid ring's settlement sector. The
     * repeat-x `near` tile (buildings sliced at both edges) stays the 2D parallax art.
     */
    settlement?: FT3DBgLayer;
}

/** Variant key used when the encounter has no encounter_backdrop_variant set. */
export const FT3D_BG_DEFAULT_VARIANT = '_default';

export interface FT3DBgManifest {
    /**
     * environments[envName][variant] (ENV_MATRIX_PLAN.md §5). `variant` is the encounter's
     * backdrop-only `encounter_backdrop_variant` string; '_default' when unspecified.
     * Fallback chain: (env,variant) → (env,'_default') → nothing.
     */
    environments: Record<string, Record<string, FT3DBgEnvSet>>;
}

/** Resolve the terrain set for (envName, variant) via the §5 fallback chain. */
export function resolveXZBgEnvSet(envName: string, variant: string): FT3DBgEnvSet | undefined {
    const byVariant = FT3D_BG_MANIFEST.environments[envName];
    if (!byVariant) return undefined;
    return byVariant[variant || FT3D_BG_DEFAULT_VARIANT] ?? byVariant[FT3D_BG_DEFAULT_VARIANT];
}

// ─── "Rooms don't repeat" pool pick (INDOOR_RENDER_PLAN.md §5) ───────────────────────────
// Deterministic per-encounter tile selection from a seed pool: a stable string hash of the
// encounter id (NOT Math.random) so every reload / every player sees the same tile for the
// same room, while different rooms of the same type draw different pool members.

/** Stable 32-bit string hash (djb2 xor-free variant; unsigned). */
export function hashStr(s: string): number {
    let h = 5381;
    for (let i = 0; i < s.length; i++) {
        h = ((h << 5) + h + s.charCodeAt(i)) >>> 0;
    }
    return h;
}

/** Deterministic pool pick — same seedKey always yields the same member. */
export function pickFromPool<T>(pool: readonly T[], seedKey: string): T | undefined {
    if (!pool.length) return undefined;
    if (pool.length === 1) return pool[0];
    return pool[hashStr(seedKey) % pool.length];
}

/**
 * Tiny deterministic PRNG (mulberry32) — seed it from hashStr once, then draw as many
 * independent values as needed. Feeds the per-encounter SEEDED TRANSFORMS in
 * FT3DParallaxBackground (X phase offset / mirror flip / micro-grade), which give single-tile
 * envs per-encounter variety without new art. The pool pick stays on pickFromPool so
 * existing pooled rooms keep the tile they already render.
 */
export function mulberry32(seed: number): () => number {
    let a = seed >>> 0;
    return () => {
        a = (a + 0x6D2B79F5) >>> 0;
        let t = a;
        t = Math.imul(t ^ (t >>> 15), t | 1);
        t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
        return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
    };
}

/**
 * Feature flag — ON by owner request 2026-07-03 for the Phase 0 staging smoke test.
 * Kill switch: in the browser console run
 *   localStorage.setItem('ft_xz_parallax', '0')   (or set window.FT_XZ_PARALLAX = false)
 * then reload. Flag home (config vs container class vs GM toggle) is still owner
 * decision §11.4; when off the whole system is a DOM/no-op.
 */
export function ft3dParallaxEnabled(): boolean {
    try {
        if ((window as unknown as { FT_XZ_PARALLAX?: unknown }).FT_XZ_PARALLAX === false) return false;
        return window.localStorage?.getItem('ft_xz_parallax') !== '0';
    } catch {
        return true;
    }
}

// ─── Procedural SVG element kits — moved to ft3d-backdrop-kits.ts ──────────────────────────
// (PROCEDURAL_BACKDROP_PLAN.md §5.1) The Phase-0 forest builders (pinePath/oakPath/ridgePath)
// and the cosmic band builders (wispBand/speckleBand/rubble/coral/kelp) now live in
// ft3d-backdrop-kits.ts as SEEDED composers. This module keeps only the manifest DATA: which
// theme uses which composer with which palette. forestNearSvg/forestFarSvg stay exported from
// the kit as the no-art fallback scaffolding (forest itself ships baked webp tiles).

// Shared cold-misty-MORNING sky (matches the forest pilot + the day composites). The band's
// TOP stop must EQUAL skyUpper or the band/upper seam shows as a hard line (owner-caught
// 2026-07-03); eased stops kill banding inside the gradient itself. A per-weather sky grid is
// Phase 2 (see FT3D_PARALLAX_BACKGROUND_PLAN.md §4.2).
const DAY_SKY: FT3DBgLayer = {
    kind: 'gradient',
    gradient: 'linear-gradient(to bottom, #1f3542 0%, #243d4b 16%, #2d4855 32%, #3b5867 48%, #4f6c7c 63%, #698795 76%, #87a0ab 87%, #a2b5ba 95%, #aebfc2 100%)',
    skyUpper: '#1f3542',
    bandFt: 300,
    parallaxX: 0,
    parallaxY: 0,
    opacity: 0.9,
};

// Shared NIGHT sky — deep blue-black nocturne, horizon a touch lighter to meet the tiles.
// Stops mirror imageforge/bg_composite.py SKY['night'] (the composites the owner approved the
// night tiles against). Same seam rule as DAY_SKY: top stop EQUALS skyUpper. This is ambient
// night ART, not darkness — the encounter_light vision mask supplies darkness on top (§3a).
const NIGHT_SKY: FT3DBgLayer = {
    kind: 'gradient',
    gradient: 'linear-gradient(to bottom, #060a12 0%, #09101c 40%, #0e1a28 70%, #142637 88%, #1c3244 100%)',
    skyUpper: '#060a12',
    bandFt: 300,
    parallaxX: 0,
    parallaxY: 0,
    opacity: 0.9,
};

// Terrain set for a side-elevation outdoor env: shared skies + painterly raster far/near tiles
// (ImageForge/Flux) at images/xz-bg/<env>/{near,far}.webp (day) and {near,far}_night.webp (night)
// (auto-SFTP to staging). v1 breadth = forest (pilot) + hill + mountain + grassland
// (ENV_MATRIX_PLAN.md §6), each day+night, variant '_default' only until variant art exists.
// Per-env heightFt/parallax are uniform for v1; tune per biome later if a set wants it. The SVG
// generator functions above stay as scaffolding.
// ⭐ Bump BG_VER on every tile re-export: the tile URLs are otherwise unversioned, so a re-pushed
// file at the same path serves STALE from browser cache (unlike bundles, which get a per-deploy
// ?v=). This query makes an art revision actually appear without a hard refresh.
// (v2 = seam re-work 2026-07-05; v3 = even-horizon far re-picks; v4 = desert+arctic added;
//  v6 = 8 biome VARIANTS; v7 = underground pack + settlement (ruins/harbor/graveyard) + moods
//  (swamp toxic, arctic aurora); v9 = AI cosmic far.webp bands for all 14 planar/space/underwater
//  variants (was procedural SVG far); v10 = full-bleed void.webp fill for outer_space + astral
//  (space above + below the X axis); v11 = void tiles regen at HQ (dpmpp_2m/30-step Flux — crisper,
//  brighter stars, richer nebula) + CSS star sparkle; v12 = de-weaved void tiles (removed the fp8
//  diagonal cross-hatch that moiréd into bold `////` when upscaled in-app — bg_post deweave()).)
// Exported for the modify_encounter scenery picker (ft3d-scenery-picker.ts), which was a PHP
// mirror of this constant until 2026-07-07 — and had drifted (thumbs stuck at v8).
/** Every backdrop/indoor tile is served from the CDN at its repo key — never the site origin (owner 2026-09-16,
 *  plans/cdn-asset-migration-2026-09-16.md). three's TextureLoader fetches with CORS; the bucket allows the
 *  fatetide origins. A re-export must be published (tools/probes/cdn_upload_repo_assets.php --dir=images/xz-bg)
 *  under a NEW filename or with a BG_VER/IND_VER bump — the edge cannot be purged. */
export const BG_ORIGIN = 'https://cdn.fatetide.com';
export const BG_VER = '16';   // v16 = owner round-3 re-picks (2026-07-10): coast clear/open-sea/riverside/
                              // ship-port nears + riverside far; arctic aurora full set incl. REAL aurora
                              // night tiles (rev8 night_near 02 + v4 day/night-far sources).
                              // v15 = alpha-key fix (2026-07-09 Fable): sky-gated sat rescue kills the
                              // semi-opaque sky shelf (owner's horizontal bands), interior-haze lift
                              // kills in-content bleed ghosts; harbor far un-sticks from the car frame.
                              // v14 = rev8 regen (2026-07-09): de-mist bright default + anti-figure/corridor
                              // hardening; 25 new variants (forest jungle/autumn/blighted/ancient/birch,
                              // grassland savanna/farmland/battlefield/flowering, mountain peaks/canyon/karst,
                              // desert dunes, hill moor/terraced, urban city/slums/camp, underground
                              // ruins/deep_roads/fungal); planes fire/feywild/shadowfell/infernal/water/earth/
                              // air promoted cosmicSet→envSet (real near+far packs); coast ship/airship split
                              // into port variants (old keys aliased); concept-fix re-picks (cliffs/harbor/
                              // graveyard/mesa/mangrove/toxic/glacier); void singles re-picked.
                              // v13 = flatten_macro on void tiles (even the nebula/astral brightness so space
                              // reads uniform — the bright-band-vs-dark two-tone read as a false horizon).
// Dark cave "sky" for underground — the morning-haze DAY_SKY would paint a grey sky above a
// dungeon wall. Near-black with a faint lift toward the groundline for depth.
const UNDERGROUND_SKY: FT3DBgLayer = {
    kind: 'gradient',
    gradient: 'linear-gradient(to bottom, #04060a 0%, #070a10 55%, #0b0f15 100%)',
    skyUpper: '#04060a', bandFt: 300, parallaxX: 0, parallaxY: 0, opacity: 0.95,
};
// Single-tile vs seed-pool layer builder shared by envSet / cosmicSet / voidSet: `url` is the
// un-versioned single-tile path ('…/far.webp'); pool>1 swaps it for the `_01.._NN` urlPool
// (2-digit, same dir), picked per encounter by pickFromPool ("rooms don't repeat", §5).
// 1/absent = today's single-tile file. Flip a slot to pooled ONLY once its _01.._NN exports
// actually exist on the CDN path.
function tileLayer(url: string, pool: number, shared: FT3DBgBase): FT3DBgLayer {
    if (pool > 1) {
        return {
            kind: 'pool',
            urlPool: Array.from({ length: pool }, (_, i) =>
                `${url.replace(/\.webp$/, `_${String(i + 1).padStart(2, '0')}.webp`)}?v=${BG_VER}`),
            ...shared,
        };
    }
    return { kind: 'image', url: `${url}?v=${BG_VER}`, ...shared };
}

// A non-'_default' variant lives under images/xz-bg/<env>/<variant>/; '_default' uses the flat
// images/xz-bg/<env>/ path. Same 4 tiles (day+night × far+near) either way, unless opts.dayOnly —
// then the pack has no *_night tiles and reuses the day tile at night (underground has no day/night).
// opts.sky / opts.skyNight override the shared morning/night sky (underground gets a dark cave sky).
// opts.pool = the tileLayer seed-pool size, applied to far AND near (day + night alike).
function envSet(env: string, variant?: string, opts?: { sky?: FT3DBgLayer; skyNight?: FT3DBgLayer; dayOnly?: boolean; pool?: number; settlement?: boolean }): FT3DBgEnvSet {
    const base = variant ? `${BG_ORIGIN}/images/xz-bg/${env}/${variant}` : `${BG_ORIGIN}/images/xz-bg/${env}`;
    const nsfx = opts?.dayOnly ? '' : '_night';
    const tile = (stem: string, shared: FT3DBgBase): FT3DBgLayer =>
        tileLayer(`${base}/${stem}.webp`, opts?.pool ?? 1, shared);
    const FAR:  FT3DBgBase = { heightFt: 90, parallaxX: 0.15, parallaxY: 0, blurPx: 1.5, opacity: 0.85 };
    const NEAR: FT3DBgBase = { heightFt: 50, parallaxX: 0.45, parallaxY: 0, opacity: 0.95 };
    return {
        sky: opts?.sky ?? DAY_SKY,
        far:  tile('far', FAR),
        near: tile('near', NEAR),
        skyNight: opts?.skyNight ?? NIGHT_SKY,
        farNight:  tile(`far${nsfx}`, FAR),
        nearNight: tile(`near${nsfx}`, NEAR),
        ...(opts?.settlement ? { settlement: tileLayer(`${base}/settlement_v2.webp`, 1, NEAR) } : {}),
    };
}
const UG = { sky: UNDERGROUND_SKY, skyNight: UNDERGROUND_SKY, dayOnly: true } as const;

// Bespoke dark skies for the abstract COSMIC envs (planar/space/underwater). Same sky day+night —
// these planes have no diurnal cycle (cosmicSet reuses the day tiles at night, like `dayOnly`).
const PLANAR_SKY:  FT3DBgLayer = { kind: 'gradient', gradient: 'linear-gradient(to bottom, #0a0812 0%, #140a1a 60%, #1c1024 100%)', skyUpper: '#0a0812', bandFt: 300, parallaxX: 0, parallaxY: 0, opacity: 0.95 };
const SPACE_SKY:   FT3DBgLayer = { kind: 'gradient', gradient: 'linear-gradient(to bottom, #01010a 0%, #03030c 70%, #060612 100%)', skyUpper: '#01010a', bandFt: 300, parallaxX: 0, parallaxY: 0, opacity: 1 };
const WATER_SKY:   FT3DBgLayer = { kind: 'gradient', gradient: 'linear-gradient(to bottom, #04121e 0%, #072436 60%, #0c3a52 100%)', skyUpper: '#04121e', bandFt: 300, parallaxX: 0, parallaxY: 0, opacity: 0.95 };

// ─── COSMIC tiling placeholders (SKYBOX_REFACTOR_HANDOFF.md, owner 2026-07-06) ───────────
// planar / outer_space / underwater now use the SAME tiling sky/far/near pattern as forest —
// the owner-identified quality path ("forest is the best-looking backdrop we have — because
// it's the tiling system, not a single image"). The single-image matte.webp `cover` path is
// DELETED: one 2048×1024 stretched across the band was low-res and cropped at zoom extremes
// (and the viewport-skybox attempt washed white under the canvas-scoped light overlay).
// Until ImageForge's tileable exports land, far/near are PROCEDURAL SVG bands composed from
// the element kit (ft3d-backdrop-kits.ts): seamless-X, deterministic, crisp at any zoom,
// per-variant palettes — and SEEDED per encounter (PROCEDURAL_BACKDROP_PLAN.md §1/§5.2), so
// two rooms of the same variant compose distinct but on-theme scenes. When
// images/xz-bg/<env>/<variant>/{far,near}.webp exist on the CDN, swap that variant's
// cosmicSet(...) for envSet(env, variant, { sky, skyNight: sky, dayOnly: true }) + a BG_VER
// bump. (matte.webp files stay on the CDN — the modify_encounter scenery-picker thumbs still
// read them until the near.webp exports replace them.)

/** Cosmic env set (HYBRID, 2026-07-07): AI far.webp band (ImageForge/Flux, seam+cosmic-alpha) under
 * a bespoke sky, with the seeded SVG NEAR silhouette kept in front. far was procedural SVG until the
 * AI far-bands landed (ART_PIPELINE_HANDOFF §5); the SVG far composers stay defined as fallback
 * scaffolding. Fallback contract: a missing far.webp just drops the far band — sky + near still
 * render. Day tiles reused at night (these planes have no diurnal cycle). */
function cosmicSet(sky: FT3DBgLayer, farUrl: string, nearFn: FT3DSvgComposer, opts?: { pool?: number }): FT3DBgEnvSet {
    return {
        sky,
        // opts.pool → far_01..far_NN seed pool once the pooled band exports exist (tileLayer).
        far: tileLayer(farUrl, opts?.pool ?? 1, { heightFt: 90, parallaxX: 0.15, parallaxY: 0, opacity: 0.9 }),
        near: { kind: 'svg', svgFn: nearFn, heightFt: 50, parallaxX: 0.45, parallaxY: 0, opacity: 0.95 },
        skyNight: sky,
    };
}

/** Full-bleed VOID set (2026-07-07, owner): open-space envs (outer_space + astral) where there is no
 * horizon and no ground — a single seamless-XY `void.webp` fills the WHOLE canvas (above + below the
 * X axis, tiled both ways), no near silhouette. `sky` is kept only as an opaque-fail base color. */
function voidSet(sky: FT3DBgLayer, url: string, opts?: { pool?: number }): FT3DBgEnvSet {
    return {
        sky,
        // opts.pool → void_01..void_NN seed pool once the pooled void exports exist (tileLayer).
        far: tileLayer(url, opts?.pool ?? 1, { fillCanvas: true, heightFt: 100, parallaxX: 0.08, parallaxY: 0.04, opacity: 1 }),
        skyNight: sky,
    };
}

// Per-variant assemblies — distinct palettes/geometry per variant (the "Tundra/Glacier all look
// the same" trap from ENV_MATRIX_PLAN §2 applies to placeholders too). Each composer ADDS the
// bounded per-encounter seed to its per-variant base seeds — seed 0 reproduces the pre-seeding
// fixed art exactly, any other seed reshuffles the layout within the variant's palette.
const COSMIC_SETS = {
    // FAR = AI far.webp bands (2026-07-07, Fable): Flux-generated seamless texture strips (seam-blend
    // + positional cosmic-alpha, ART_PIPELINE_HANDOFF §3-4), one per variant, replacing the procedural
    // SVG far. NEAR stays the seeded SVG silhouette (hybrid). Prompts are flat-allover textures (no
    // vista/focal-centre — the first pass's negation-summons horizon-bloom was rejected + re-prompted).
    // PLANES PROMOTED cosmicSet→envSet (rev8, 2026-07-09): each now has a real AI near.webp + far.webp
    // pack (dramatic plane vistas), dayOnly (planes have no diurnal cycle → day tiles reused at night),
    // under the bespoke PLANAR_SKY. astral stays a full-bleed void. The old SVG-near cosmicSet is gone.
    'planar': {
        'fire':       envSet('planar', 'fire',       { sky: PLANAR_SKY, skyNight: PLANAR_SKY, dayOnly: true }),
        // astral = open void (owner 2026-07-07): full-bleed space, no horizon/ground/near.
        'astral':     voidSet(PLANAR_SKY, `${BG_ORIGIN}/images/xz-bg/planar/astral/void.webp`),
        'feywild':    envSet('planar', 'feywild',    { sky: PLANAR_SKY, skyNight: PLANAR_SKY, dayOnly: true }),
        'shadowfell': envSet('planar', 'shadowfell', { sky: PLANAR_SKY, skyNight: PLANAR_SKY, dayOnly: true }),
        'infernal':   envSet('planar', 'infernal',   { sky: PLANAR_SKY, skyNight: PLANAR_SKY, dayOnly: true }),
        // Elemental planes (2026-07-07) — Water / Earth / Air.
        'water':      envSet('planar', 'water',      { sky: PLANAR_SKY, skyNight: PLANAR_SKY, dayOnly: true }),
        'earth':      envSet('planar', 'earth',      { sky: PLANAR_SKY, skyNight: PLANAR_SKY, dayOnly: true }),
        'air':        envSet('planar', 'air',        { sky: PLANAR_SKY, skyNight: PLANAR_SKY, dayOnly: true }),
    },
    // outer_space = open void (owner 2026-07-07): full-bleed star/nebula fill, above + below the axis.
    'outer_space': {
        'starfield': voidSet(SPACE_SKY, `${BG_ORIGIN}/images/xz-bg/outer_space/starfield/void.webp`),
        'nebula':    voidSet(SPACE_SKY, `${BG_ORIGIN}/images/xz-bg/outer_space/nebula/void.webp`),
        'asteroid':  voidSet(SPACE_SKY, `${BG_ORIGIN}/images/xz-bg/outer_space/asteroid/void.webp`),
    },
    'underwater': {
        'reef':  cosmicSet(WATER_SKY, `${BG_ORIGIN}/images/xz-bg/underwater/reef/far.webp`,  (s) => coralNearSvg(boundSeed(s))),
        'kelp':  cosmicSet(WATER_SKY, `${BG_ORIGIN}/images/xz-bg/underwater/kelp/far.webp`,  (s) => kelpNearSvg(boundSeed(s))),
        'abyss': cosmicSet(WATER_SKY, `${BG_ORIGIN}/images/xz-bg/underwater/abyss/far.webp`, (s) => rubbleNearSvg('#06131c', '#030a10', 0.05, 221 + boundSeed(s))),
    },
} satisfies Record<string, Record<string, FT3DBgEnvSet>>;

// Formal FOREST kit hookup (plan §5.1): the seeded forest composers are the no-art fallback
// for any future SVG-only forest-like theme. Referenced here (not yet wired into a manifest
// entry — forest ships baked webp tiles, which stay the richer path per the fallback contract).
export const FT3D_FOREST_KIT: { far: FT3DSvgComposer; near: FT3DSvgComposer } = {
    far: forestFarSvg,
    near: forestNearSvg,
};

export const FT3D_BG_MANIFEST: FT3DBgManifest = {
    environments: {
        'forest': {
            [FT3D_BG_DEFAULT_VARIANT]: envSet('forest'),
            'pine':      envSet('forest', 'pine'),
            'mountains': envSet('forest', 'mountains'),
            'jungle':    envSet('forest', 'jungle'),
            'autumn':    envSet('forest', 'autumn'),
            'blighted':  envSet('forest', 'blighted'),
            'ancient':   envSet('forest', 'ancient'),
            'birch':     envSet('forest', 'birch'),
        },
        'hill': {
            [FT3D_BG_DEFAULT_VARIANT]: envSet('hill'),
            'moor':     envSet('hill', 'moor'),
            'terraced': envSet('hill', 'terraced'),
        },
        'mountain': {
            [FT3D_BG_DEFAULT_VARIANT]: envSet('mountain'),
            'misty':         envSet('mountain', 'misty'),   // pre-rev8 hazy default, kept selectable
            'volcanic':      envSet('mountain', 'volcanic'),
            'alpine_forest': envSet('mountain', 'alpine_forest'),
            'peaks':         envSet('mountain', 'peaks'),
            'canyon':        envSet('mountain', 'canyon'),
            'karst':         envSet('mountain', 'karst'),
        },
        'grassland': {
            [FT3D_BG_DEFAULT_VARIANT]: envSet('grassland'),
            'misty':       envSet('grassland', 'misty'),
            'savanna':     envSet('grassland', 'savanna'),
            'farmland':    envSet('grassland', 'farmland'),
            'battlefield': envSet('grassland', 'battlefield'),
            'flowering':   envSet('grassland', 'flowering'),
        },
        'desert': {
            [FT3D_BG_DEFAULT_VARIANT]: envSet('desert'),
            'misty': envSet('desert', 'misty'),
            'mesa':  envSet('desert', 'mesa'),
            'dunes': envSet('desert', 'dunes'),
        },
        'arctic': {
            [FT3D_BG_DEFAULT_VARIANT]: envSet('arctic'),
            'glacier': envSet('arctic', 'glacier'),
            'aurora':  envSet('arctic', 'aurora'),   // night = aurora; day = clear snow
        },
        'swamp': {
            [FT3D_BG_DEFAULT_VARIANT]: envSet('swamp'),
            'misty':    envSet('swamp', 'misty'),
            'mangrove': envSet('swamp', 'mangrove'),
            'toxic':    envSet('swamp', 'toxic'),    // vivid bioluminescent green
        },
        'coast': {
            [FT3D_BG_DEFAULT_VARIANT]: envSet('coast'),
            'misty':        envSet('coast', 'misty'),
            'cliffs':       envSet('coast', 'cliffs'),
            // ship/airship SPLIT (rev8): port variants ship reliably (deck + harbor); sea/sky nears
            // did not render a deck (deferred). Old 'ship'/'airship' keys aliased to the port sets so
            // saved encounters using them don't blank.
            'ship_port':    envSet('coast', 'ship_port'),
            'airship_port': envSet('coast', 'airship_port'),
            'open_sea':     envSet('coast', 'open_sea'),
            'riverside':    envSet('coast', 'riverside'),
            'ship':         envSet('coast', 'ship_port'),      // legacy alias
            'airship':      envSet('coast', 'airship_port'),   // legacy alias
        },
        // Urban has no flat 'Misty' default (there was never a plain urban backdrop) — the castle
        // is the default so env=urban always renders something. All three sit under variant subdirs.
        'urban': {
            // settlement: the FT3D horizon ring's vignette strips (ImageForge `--layer settlement`, 2026-09-27).
            [FT3D_BG_DEFAULT_VARIANT]: envSet('urban', 'castle', { settlement: true }),
            'castle':    envSet('urban', 'castle', { settlement: true }),
            'town':      envSet('urban', 'town', { settlement: true }),
            'house':     envSet('urban', 'house', { settlement: true }),
            'ruins':     envSet('urban', 'ruins'),
            'harbor':    envSet('urban', 'harbor'),
            'graveyard': envSet('urban', 'graveyard'),
            'city':      envSet('urban', 'city', { settlement: true }),
            'slums':     envSet('urban', 'slums', { settlement: true }),
            'camp':      envSet('urban', 'camp'),
        },
        // Underground pack (owner priority) — dark cave sky, day-only tiles (no day/night sky).
        // Default = cave so env=underground always renders.
        'underground': {
            [FT3D_BG_DEFAULT_VARIANT]: envSet('underground', 'cave', { ...UG, pool: 3 }),
            'cave':    envSet('underground', 'cave',    { ...UG, pool: 3 }),
            'dungeon': envSet('underground', 'dungeon', { ...UG, pool: 3 }),
            'crypt':   envSet('underground', 'crypt',   { ...UG, pool: 3 }),
            'mine':    envSet('underground', 'mine',    { ...UG, pool: 2 }),
            // rev8 new packs (single-tile, pool 1). deep_roads/fungal/ruins have near+far.
            'ruins':      envSet('underground', 'ruins',      UG),
            'deep_roads': envSet('underground', 'deep_roads', UG),
            'fungal':     envSet('underground', 'fungal',     UG),
        },
        // Abstract COSMIC envs — tiling sky/far/near like forest (SKYBOX_REFACTOR_HANDOFF.md).
        // Procedural SVG placeholder tiles until the ImageForge exports land; swap per-variant
        // to envSet(env, variant, { sky, skyNight: sky, dayOnly: true }) as the webps arrive.
        'planar': {
            [FT3D_BG_DEFAULT_VARIANT]: COSMIC_SETS.planar.fire,
            ...COSMIC_SETS['planar'],
        },
        'outer_space': {
            [FT3D_BG_DEFAULT_VARIANT]: COSMIC_SETS.outer_space.starfield,
            ...COSMIC_SETS['outer_space'],
        },
        'underwater': {
            [FT3D_BG_DEFAULT_VARIANT]: COSMIC_SETS.underwater.reef,
            ...COSMIC_SETS['underwater'],
        },
    },
};

/**
 * Human-facing variant catalog for the ft3d_scene_host.php scenery picker (ENV_MATRIX_PLAN §2).
 * Keyed by environment; each entry is [variantKey, label]. variantKey '' = the shipped Misty
 * default (encounter_backdrop_variant NULL/empty). Labels follow the "<Type>, <Style>" scheme.
 * The picker reads this to render selectable tiles; only envs with >1 entry need a picker shown.
 */
export const FT3D_BG_VARIANT_CATALOG: Record<string, Array<[string, string]>> = {
    'forest':   [['', 'Misty'], ['pine', 'Pine Taiga'], ['mountains', 'Mountains'], ['jungle', 'Jungle'], ['autumn', 'Autumn'], ['blighted', 'Blighted'], ['ancient', 'Ancient'], ['birch', 'Birch']],
    'mountain': [['', 'Clear'], ['misty', 'Misty'], ['volcanic', 'Volcanic'], ['alpine_forest', 'Alpine Forest'], ['peaks', 'Peaks'], ['canyon', 'Canyon'], ['karst', 'Karst']],
    'desert':   [['', 'Clear'], ['misty', 'Misty'], ['mesa', 'Mesa Canyon'], ['dunes', 'Dunes']],
    'arctic':   [['', 'Misty'], ['glacier', 'Glacier'], ['aurora', 'Aurora']],
    'coast':    [['', 'Clear'], ['misty', 'Misty'], ['cliffs', 'Sea Cliffs'], ['ship_port', 'Ship (Port)'], ['airship_port', 'Airship (Port)'], ['open_sea', 'Open Sea'], ['riverside', 'Riverside']],
    'swamp':    [['', 'Clear'], ['misty', 'Misty'], ['mangrove', 'Mangrove'], ['toxic', 'Toxic Bloom']],
    'urban':    [['', 'Castle Exterior'], ['town', 'Town'], ['house', 'Hamlet'], ['ruins', 'Ruins'], ['harbor', 'Harbor'], ['graveyard', 'Graveyard'], ['city', 'City'], ['slums', 'Slums'], ['camp', 'Camp']],
    'underground': [['', 'Cave'], ['dungeon', 'Dungeon'], ['crypt', 'Crypt'], ['mine', 'Mine'], ['ruins', 'Ruins'], ['deep_roads', 'Deep Roads'], ['fungal', 'Fungal']],
    'planar':      [['', 'Fire'], ['astral', 'Astral'], ['feywild', 'Feywild'], ['shadowfell', 'Shadowfell'], ['infernal', 'Infernal'], ['water', 'Water'], ['earth', 'Earth'], ['air', 'Air']],
    'outer_space': [['', 'Starfield'], ['nebula', 'Nebula'], ['asteroid', 'Asteroid']],
    'underwater':  [['', 'Reef'], ['kelp', 'Kelp'], ['abyss', 'Abyss']],
    'hill':      [['', 'Misty'], ['moor', 'Moor'], ['terraced', 'Terraced']],
    'grassland': [['', 'Clear'], ['misty', 'Misty'], ['savanna', 'Savanna'], ['farmland', 'Farmland'], ['battlefield', 'Battlefield'], ['flowering', 'Flowering']],
};

// ─── INDOOR interior materials (INDOOR_RENDER_PLAN.md) ──────────────────────────────────
// Tiled wall/floor/ceiling materials per BUILDING/ROOM TYPE (encounter_interior_type — a GM
// pick independent of the outdoor environment). Served from images/xz-bg/indoor/<type>/
// {wall,floor,ceiling}.webp (same ImageForge ground-mode + make_seamless_xy pipeline as the
// outdoor ground textures). FALLBACK CONTRACT: every slot carries flat fallback COLORS that
// the room can use beneath a texture when a webp is unavailable.
// "Rooms don't repeat" (§5): each slot is a POOL of N tiles (wall_01..wall_NN when N>1),
// picked per encounter via pickFromPool(pool, encounterId). All pools ship at N=1 until the
// pooled exports exist — bump INDOOR_POOL_COUNTS only after the files are on the CDN path.

/** Cache-bust for the indoor material tiles — bump on every re-export (same rule as BG_VER).
 *  Exported for the picker (ft3dIndoorPickerTiles) — no PHP mirror to drift. */
export const IND_VER = '4';   // v4 = owner-supplied Rock Surface material intake (query-only option)
                              // v3 = rev8 wall re-export for tavern/great_hall/shop/throne_room
                              // (6-tile pools from the new HQ wall gen). Other types unchanged.

export interface FT3DIndoorSet {
    /** Seed pools ("rooms don't repeat", §5) — pickFromPool(pool, encounterId) selects one. */
    wallPool: string[];
    floorPool: string[];
    ceilingPool: string[];
    /** Flat fallback colors painted UNDER the textures (visible until art lands / on 404). */
    wallColor: { base: string; dark: string };
    floorColor: string;
    ceilingColor: string;
    /** Optional full PBR/provenance receipt for 3D-capable environment consumers. */
    materialManifest?: string;
}

/** Per-type pool sizes (§5). ALL 1 until Opus's pooled interior exports land on staging. */
// Opus interior packs landed 2026-07-06 (v5): wall pool 6, floor/ceiling pool 4 per type.
// (shop + throne_room WALLS are watch-items — Flux kept drawing the whole room; pool rides for now.)
const INDOOR_POOL_COUNTS: Record<string, { wall?: number; floor?: number; ceiling?: number }> = {
    tavern: { wall: 6, floor: 4, ceiling: 4 }, great_hall: { wall: 6, floor: 4, ceiling: 4 },
    house: { wall: 6, floor: 4, ceiling: 4 }, shop: { wall: 6, floor: 4, ceiling: 4 },
    temple: { wall: 6, floor: 4, ceiling: 4 }, dungeon_room: { wall: 6, floor: 4, ceiling: 4 },
    cave_chamber: { wall: 6, floor: 4, ceiling: 4 }, crypt_room: { wall: 6, floor: 4, ceiling: 4 },
    ship_interior: { wall: 6, floor: 4, ceiling: 4 }, throne_room: { wall: 6, floor: 4, ceiling: 4 },
    library: { wall: 6, floor: 4, ceiling: 4 }, prison: { wall: 6, floor: 4, ceiling: 4 },
};

function indoorSet(
    type: string,
    wallColor: { base: string; dark: string },
    floorColor: string,
    ceilingColor: string,
    materialManifest?: string,
): FT3DIndoorSet {
    const base = `${BG_ORIGIN}/images/xz-bg/indoor/${type}`;
    const counts = INDOOR_POOL_COUNTS[type] ?? {};
    const pool = (stem: 'wall' | 'floor' | 'ceiling'): string[] => {
        const n = counts[stem] ?? 1;
        if (n <= 1) return [`${base}/${stem}.webp?v=${IND_VER}`];
        return Array.from({ length: n }, (_, i) => `${base}/${stem}_${String(i + 1).padStart(2, '0')}.webp?v=${IND_VER}`);
    };
    return {
        wallPool: pool('wall'),
        floorPool: pool('floor'),
        ceilingPool: pool('ceiling'),
        wallColor,
        floorColor,
        ceilingColor,
        ...(materialManifest ? { materialManifest } : {}),
    };
}

export const INDOOR_MANIFEST: Record<string, FT3DIndoorSet> = {
    'tavern':        indoorSet('tavern',        { base: '#7a5a40', dark: '#64482f' }, '#5b4028', '#4a3520'),   // warm wood + planks
    'great_hall':    indoorSet('great_hall',    { base: '#8f8a80', dark: '#767065' }, '#6e6a60', '#55504a'),   // dressed stone
    'house':         indoorSet('house',         { base: '#a89a84', dark: '#8f7f68' }, '#6b503a', '#5b4a38'),   // plaster + timber
    'shop':          indoorSet('shop',          { base: '#7d6248', dark: '#664e36' }, '#5d452f', '#4c3a26'),   // shelved wood
    'temple':        indoorSet('temple',        { base: '#c4c2ba', dark: '#a6a49b' }, '#9a978d', '#7e7c74'),   // pale marble
    'dungeon_room':  indoorSet('dungeon_room',  { base: '#5c6670', dark: '#47505a' }, '#414a54', '#333b44'),   // dark worked stone
    'cave_chamber':  indoorSet('cave_chamber',  { base: '#6e675e', dark: '#57514a' }, '#4d4740', '#3a3530'),   // raw rock
    'crypt_room':    indoorSet('crypt_room',    { base: '#8a8f94', dark: '#6f757c' }, '#5f656c', '#4a5057'),   // bone-pale masonry
    'ship_interior': indoorSet('ship_interior', { base: '#5f4736', dark: '#4c3829' }, '#43301f', '#382817'),   // tarred planks
    'throne_room':   indoorSet('throne_room',   { base: '#8a7a6a', dark: '#6f6152' }, '#6a5c4c', '#504538'),   // rich stone
    'library':       indoorSet('library',       { base: '#6f523c', dark: '#5a4230' }, '#55402c', '#453322'),   // shelved wood, darker
    'prison':        indoorSet('prison',        { base: '#62676b', dark: '#4d5256' }, '#45494d', '#35393c'),   // iron-grey
    'rock_surface':  indoorSet('rock_surface',  { base: '#51504e', dark: '#373735' }, '#444340', '#363533', '/images/xz-bg/indoor/rock_surface/material-v1.json'), // Fab PBR raw rock
};

/**
 * NULL/unknown encounter_interior_type → a sensible default per outdoor ENVIRONMENT, so old
 * indoor encounters render a plausible interior without a saved pick (plan §3).
 */
export const FT3D_INDOOR_DEFAULT_BY_ENV: Record<string, string> = {
    'underground': 'cave_chamber',
    'urban':        'tavern',
    'underwater':   'ship_interior',
    'outer_space':  'ship_interior',
    'coast':        'ship_interior',
    'planar':       'temple',
};

/** Resolve the effective interior TYPE key: saved pick → env default → 'house'. */
export function resolveIndoorType(interiorType: string, envName: string): string {
    if (interiorType && INDOOR_MANIFEST[interiorType]) return interiorType;
    return FT3D_INDOOR_DEFAULT_BY_ENV[envName] ?? 'house';
}

/** Material set for (interiorType, envName) via the same fallback contract as the backdrop. */
export function resolveIndoorSet(interiorType: string, envName: string): FT3DIndoorSet | undefined {
    return INDOOR_MANIFEST[resolveIndoorType(interiorType, envName)];
}

/**
 * Human-facing interior-type catalog for the ft3d_scene_host.php indoor picker (plan §3) —
 * same shape as FT3D_BG_VARIANT_CATALOG entries: [typeKey, label]. '' = Auto (derive from the
 * environment via FT3D_INDOOR_DEFAULT_BY_ENV). Keep in sync with the PHP picker map.
 */
export const INDOOR_TYPE_CATALOG: Array<[string, string]> = [
    ['', 'Auto'],
    ['tavern', 'Tavern'],
    ['great_hall', 'Great Hall'],
    ['house', 'House'],
    ['shop', 'Shop'],
    ['temple', 'Temple'],
    ['dungeon_room', 'Dungeon Room'],
    ['cave_chamber', 'Cave Chamber'],
    ['crypt_room', 'Crypt'],
    ['ship_interior', 'Ship Interior'],
    ['throne_room', 'Throne Room'],
    ['library', 'Library'],
    ['prison', 'Prison'],
    ['rock_surface', 'Rock Surface'],
];

export interface FT3DSurfaceCatalogRow {
    surfaceId: string;
    name: string;
    manifestUrl: string;
    sha256: string;
    bytes: number;
    quality: string;
}

interface FT3DSurfaceMaterialManifest {
    schema?: unknown;
    id?: unknown;
    maps?: { basecolor?: { url?: unknown } };
    /**
     * The quality-derivative manifest (`…-representations.v1`) the surface catalog serves today:
     * one map set per tier (low 1024 · medium 2048 · high/ultra 4096) instead of a single 4096
     * source set. Both shapes are accepted — see surfaceBasecolorUrl().
     */
    representations?: Record<string, { maps?: { basecolor?: { url?: unknown } } } | undefined>;
}

/** The WORLD pools take the best derivative available; a chip/thumbnail takes the smallest. Reading
 *  one order for both would have quietly dropped every painted wall to 1024px (Codex S13a). */
const SURFACE_WORLD_ORDER = ['high', 'ultra', 'medium', 'low'] as const;
const SURFACE_THUMBNAIL_ORDER = ['low', 'medium', 'high', 'ultra'] as const;
/** surfaceId → the small derivative a picker tile paints (the world pools keep the full-size map). */
const surfaceThumbnailUrls = new Map<string, string>();

/**
 * The basecolor URL of an approved surface manifest, whichever schema it carries: the original
 * `environment-material.v1` (one 4096 map set) or the `environment-material-representations.v1`
 * quality derivatives the catalog serves now. Returns '' when neither shape yields a CDN URL —
 * reading only the old schema silently blanked every approved surface (owner-visible: the Walls
 * finish tiles had no pictures, 2026-09-07). `order` picks the tier: world pools take the best
 * available, a picker chip the smallest.
 */
function surfaceBasecolorUrl(manifest: FT3DSurfaceMaterialManifest, order: readonly string[]): string {
    const accept = (value: unknown): string => (
        typeof value === 'string' && value.startsWith('https://cdn.fatetide.com/') ? value : ''
    );
    for (const tier of order) {
        const url = accept(manifest.representations?.[tier]?.maps?.basecolor?.url);
        if (url) return url;
    }
    return accept(manifest.maps?.basecolor?.url);
}

const surfaceCatalogPromises = new Map<string, Promise<readonly FT3DSurfaceCatalogRow[]>>();
const surfaceMaterialPromises = new Map<string, Promise<boolean>>();

/** Fetch and validate only the approved catalog index. Material manifests and images remain lazy. */
export function loadApprovedSurfaceCatalog(
    endpoint = '/ft3d/engine/surface-material-catalog.php',
): Promise<readonly FT3DSurfaceCatalogRow[]> {
    const cached = surfaceCatalogPromises.get(endpoint);
    if (cached) return cached;
    const promise = (async (): Promise<readonly FT3DSurfaceCatalogRow[]> => {
        try {
            const response = await fetch(endpoint, { credentials: 'same-origin' });
            if (!response.ok) return [];
            const catalog = await response.json() as { schema?: unknown; rows?: unknown };
            if (catalog.schema !== 'ft.xz3d.surface-material-catalog.v1' || !Array.isArray(catalog.rows)) return [];
            return catalog.rows.flatMap((candidate): FT3DSurfaceCatalogRow[] => {
                const row = candidate as Partial<FT3DSurfaceCatalogRow>;
                const surfaceId = String(row.surfaceId ?? '');
                const name = typeof row.name === 'string' ? row.name.trim() : '';
                const manifestUrl = typeof row.manifestUrl === 'string' ? row.manifestUrl : '';
                // Match the native registry's semantic ID contract, independent of provider.
                if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{0,35}$/.test(surfaceId)
                    || name === ''
                    || !manifestUrl.startsWith('https://cdn.fatetide.com/')) return [];
                return [{
                    surfaceId,
                    name,
                    manifestUrl,
                    sha256: typeof row.sha256 === 'string' ? row.sha256 : '',
                    bytes: Number.isFinite(Number(row.bytes)) ? Number(row.bytes) : 0,
                    quality: typeof row.quality === 'string' ? row.quality : '',
                }];
            });
        } catch {
            return [];
        }
    })();
    surfaceCatalogPromises.set(endpoint, promise);
    return promise;
}

/** Register one selected/visible approved material. This is the manifest hydration boundary. */
export function loadApprovedSurfaceMaterial(
    surfaceId: string,
    endpoint = '/ft3d/engine/surface-material-catalog.php',
): Promise<boolean> {
    if (INDOOR_MANIFEST[surfaceId]) return Promise.resolve(true);
    const cacheKey = `${endpoint}|${surfaceId}`;
    const cached = surfaceMaterialPromises.get(cacheKey);
    if (cached) return cached;
    const promise = (async (): Promise<boolean> => {
        const row = (await loadApprovedSurfaceCatalog(endpoint))
            .find(candidate => candidate.surfaceId === surfaceId);
        if (!row) return false;
        try {
            const manifestResponse = await fetch(row.manifestUrl, { mode: 'cors' });
            if (!manifestResponse.ok) return false;
            const manifest = await manifestResponse.json() as FT3DSurfaceMaterialManifest;
            const basecolor = surfaceBasecolorUrl(manifest, SURFACE_WORLD_ORDER);
            if (manifest.schema !== 'ft.xz3d.environment-material.v1'
                && manifest.schema !== 'ft.xz3d.environment-material-representations.v1') return false;
            if (manifest.id !== surfaceId || !basecolor) return false;
            const thumbnail = surfaceBasecolorUrl(manifest, SURFACE_THUMBNAIL_ORDER);
            if (thumbnail) surfaceThumbnailUrls.set(surfaceId, thumbnail);
            INDOOR_MANIFEST[surfaceId] = {
                wallPool: [basecolor],
                floorPool: [basecolor],
                ceilingPool: [basecolor],
                wallColor: { base: '#68645d', dark: '#45423e' },
                floorColor: '#55514b',
                ceilingColor: '#45423e',
                materialManifest: row.manifestUrl,
            };
            if (!INDOOR_TYPE_CATALOG.some(([key]) => key === surfaceId)) {
                INDOOR_TYPE_CATALOG.push([surfaceId, row.name]);
            }
            return true;
        } catch {
            return false;
        }
    })();
    surfaceMaterialPromises.set(cacheKey, promise);
    return promise;
}

/** A picker tile paints the SMALL derivative when the manifest offers one; else the world map. */
export function approvedSurfaceThumbnail(surfaceId: string): string {
    return surfaceThumbnailUrls.get(surfaceId) ?? INDOOR_MANIFEST[surfaceId]?.floorPool[0] ?? '';
}

/**
 * Hydrate the approved database-backed surface catalog into the existing indoor material
 * resolver. Pending/rejected rows never reach this endpoint. The full PBR manifest remains
 * available to 3D consumers while the XZ map uses base color as its tiled wall/floor/ceiling.
 */
export function loadApprovedSurfaceMaterials(
    endpoint = '/ft3d/engine/surface-material-catalog.php',
): Promise<number> {
    return (async (): Promise<number> => {
        const rows = await loadApprovedSurfaceCatalog(endpoint);
        let registered = 0;
        for (const row of rows) {
            if (await loadApprovedSurfaceMaterial(row.surfaceId, endpoint)) registered++;
        }
        return registered;
    })();
}

// ─── Picker tile data (ft3d_scene_host.php scenery/interior pickers) ─────────────────────
// Moved out of ft3d_scene_host.php 2026-07-07: the form used to hand-maintain PHP mirrors of
// the catalogs + versions above ("keep in sync" comments), and the version constant had already
// drifted (thumbs cache-busting at v8 vs BG_VER 13). ft3d-scenery-picker.ts renders the pickers
// client-side from these builders, so the manifest is the ONLY source of picker data.

/** One selectable picker tile. `color` is the flat fallback painted under indoor thumbs. */
export interface FT3DPickerTile { key: string; label: string; thumb: string; color?: string }

// Default-tile presentation for envs whose '' (default) art is not the flat 'Misty' set —
// which variant subdir the default thumb reads (the label already lives in the catalog).
const BG_DEFAULT_THUMB_VARIANT: Record<string, string> = {
    'urban': 'castle',
    'underground': 'cave',
    'planar': 'fire',
    'outer_space': 'starfield',
    'underwater': 'reef',
};
// Matte-plane envs store a single matte.webp (not near/far) — picker thumbs read that (the
// matte files stay on the CDN for exactly this even though the map no longer renders them).
const BG_THUMB_FILE_BY_ENV: Record<string, string> = {
    'planar': 'matte.webp', 'outer_space': 'matte.webp', 'underwater': 'matte.webp',
};

/** Ordered scenery tiles for an env (default first) — from FT3D_BG_VARIANT_CATALOG. [] when the
 *  env has no catalog entry (the picker renders its blank Misty fallback). */
export function ft3dBgPickerTiles(envName: string): FT3DPickerTile[] {
    const catalog = FT3D_BG_VARIANT_CATALOG[envName];
    if (!catalog) return [];
    const tf = BG_THUMB_FILE_BY_ENV[envName] ?? 'near.webp';
    const defVariant = BG_DEFAULT_THUMB_VARIANT[envName];
    return catalog.map(([key, label]) => {
        const dir = key
            ? `${BG_ORIGIN}/images/xz-bg/${envName}/${key}`
            : (defVariant ? `${BG_ORIGIN}/images/xz-bg/${envName}/${defVariant}` : `${BG_ORIGIN}/images/xz-bg/${envName}`);
        return { key, label, thumb: `${dir}/${tf}?v=${BG_VER}` };
    });
}

/** Ordered interior tiles ('' = Auto first) — INDOOR_TYPE_CATALOG labels + INDOOR_MANIFEST
 *  art (thumb = the wall pool's first member, so it always matches shipped art). */
export function ft3dIndoorPickerTiles(): FT3DPickerTile[] {
    return INDOOR_TYPE_CATALOG.map(([key, label]) => {
        if (!key) return { key, label, thumb: '', color: '#2a2f36' };
        const set = INDOOR_MANIFEST[key];
        return {
            key,
            label,
            thumb: set?.wallPool[0] ?? '',
            color: set?.wallColor.base ?? '#2a2f36',
        };
    });
}

// ─── Campaign SunState → day/night (art-swap signal; ENV_MATRIX_PLAN.md §3) ─────────────
// Mirrors the server's ft_classify_campaign_sun_state_day_night() + the client ambience
// resolver's FT_SUN_STATE_NIGHT_IDS (side-panels-init.ts): night = sun_state_night|sunset,
// anything else (day, sunrise, no campaign clock, unknown) = day. Reads the cached campaign
// blob the websocket campaign_update handler maintains — the same signal that swaps
// encounter_amb_night audio, so art + audio flip together on a sun-state tick.
const FT_SUN_STATE_NIGHT_IDS = [
    'E4B7F803-C689-A9A5-0FB7-EB8D9946874E', // sun_state_night
    '7BA5009E-B362-AE2F-ABB6-D06D54D306EA', // sun_state_sunset
];

interface BackdropCampaignRoot {
    campaign?: { id?: unknown };
    campaignCache?: Map<string, unknown>;
    campaignArr?: unknown;
}

function recordOf(value: unknown): Record<string, unknown> | null {
    return value !== null && typeof value === 'object'
        ? value as Record<string, unknown>
        : null;
}

/**
 * Read one campaigns-bucket scalar from the cached campaign blob (websocket-maintained),
 * with catalog-shell unwrap ({items:{item_id}} → UUID) + legacy top-level [0] fallback —
 * same shape rules as campaign_field()/ftReadBucketScalar. '' when no campaign/blob/value.
 */
function ft3dCampaignField(col: string): string {
    try {
        const ft = window.FateTide as unknown as BackdropCampaignRoot | undefined;
        const campaignId = String(ft?.campaign?.id ?? '');
        const cached = campaignId && ft?.campaignCache instanceof Map
            ? ft.campaignCache.get(campaignId)
            : undefined;
        const campaignArr = recordOf(cached) ?? recordOf(ft?.campaignArr);
        if (!campaignArr) return '';
        const campaignBucket = recordOf(campaignArr.campaigns);
        let v: unknown = campaignBucket ? campaignBucket[col] : campaignArr[col];
        if (Array.isArray(v)) v = v[0];
        if (v && typeof v === 'object') {
            const items = (v as { items?: { item_id?: unknown } }).items;
            v = items && typeof items.item_id === 'string' ? items.item_id : '';
        }
        return String(v ?? '');
    } catch {
        return '';
    }
}

export function ft3dCampaignIsNight(): boolean {
    // Uppercase before comparing (parity with the ambience resolver's checkIfValidUUID-adjacent
    // handling): catalog item_ids are uppercase, but a lower/mixed-case value from any path must
    // not silently skip the night art while the audio still swaps.
    return FT_SUN_STATE_NIGHT_IDS.includes(ft3dCampaignField('campaign_sun_state').toUpperCase());
}

// Campaign month_season catalog UUIDs → season keys (shared catalog; the encounter-summary
// widget renders these same ids via iconMap). Unknown/empty → '' (no seasonal tint).
const FT_SEASON_BY_ID: Record<string, 'spring' | 'summer' | 'autumn' | 'winter'> = {
    '8D31485E-8590-7829-2918-2BEA0F1C125C': 'spring',
    'C3A3E1D8-1393-370F-C3D5-FEA77B0EB518': 'summer',
    '69DDD63C-1DEF-A3C2-DF5C-D022747821CA': 'autumn',
    '467B3256-C3F2-3426-217D-A8A779ED62FB': 'winter',
};

/** Current campaign season key ('' when no campaign clock / unknown). */
export function ft3dCampaignSeason(): string {
    return FT_SEASON_BY_ID[ft3dCampaignField('month_season').toUpperCase()] ?? '';
}

// ─── Backdrop PALETTE dials (owner 2026-07-06) ──────────────────────────────────────────────
// Recolour / desaturate / hide the parallax backdrop WITHOUT new art — the "give the user a few
// knobs, mostly automated" ask + the desaturate/off clash safety-valve. A viewer-level pref stored
// in localStorage (ft_xz_bg_palette); FT3DParallaxBackground stamps the matching CSS class on the
// root each rebuild, and window.ftSetBackdropPalette() flips it live. 'auto' currently leaves the
// art as authored — the automation hook (derive warm/cool from season + weather + sun-state, which
// already drive the widget) is intentionally centralised in resolveAutoPalette() below to extend.
export type FT3DBgPalette = 'auto' | 'vivid' | 'warm' | 'cool' | 'muted' | 'off';
const FT3D_PALETTE_CLASS: Record<Exclude<FT3DBgPalette, 'auto'>, string> = {
    vivid: 'xz-pal-vivid', warm: 'xz-pal-warm', cool: 'xz-pal-cool', muted: 'xz-pal-muted', off: 'xz-bg-off',
};
const FT3D_PALETTE_ALL_CLASSES = Object.values(FT3D_PALETTE_CLASS);

export function ft3dBackdropPaletteMode(): FT3DBgPalette {
    try {
        const v = window.localStorage?.getItem('ft_xz_bg_palette');
        return (['auto', 'vivid', 'warm', 'cool', 'muted', 'off'] as string[]).includes(v ?? '')
            ? (v as FT3DBgPalette) : 'auto';
    } catch {
        return 'auto';
    }
}

// Weather variable_names (env-types.ts WEATHER_TYPE_LIST) → auto-palette buckets. Cold
// precipitation reads cool; overcast/storm/haze reads muted (desaturated). Anything else
// falls through to sun-state/season below.
const FT_AUTO_COOL_WEATHER = new Set([
    'blizzard', 'snowing', 'heavy_snow', 'freezing_cold', 'hail', 'snowing_and_raining', 'chilly',
]);
const FT_AUTO_MUTED_WEATHER = new Set([
    'raining', 'heavy_rain', 'lightning_and_rain', 'cloudy', 'cloudy_lightning', 'hazy',
    'ashfall', 'dust_storm', 'tornado',
]);

/**
 * Automation hook for mode 'auto' (PROCEDURAL_BACKDROP_PLAN.md §3): derive the tint from the
 * signals the widget already tracks — encounter WEATHER (variable_name, e.g. 'heavy_rain'),
 * campaign SUN-STATE (isNight), campaign SEASON (month_season). Priority: weather extreme >
 * night > season > as-authored (''). "Settings carry the motif, colours carry the mood."
 */
function resolveAutoPalette(isNight: boolean, weatherVar: string): string {
    if (FT_AUTO_COOL_WEATHER.has(weatherVar))  return FT3D_PALETTE_CLASS.cool;
    if (FT_AUTO_MUTED_WEATHER.has(weatherVar)) return FT3D_PALETTE_CLASS.muted;
    if (isNight) return FT3D_PALETTE_CLASS.cool;
    const season = ft3dCampaignSeason();
    if (season === 'winter') return FT3D_PALETTE_CLASS.cool;
    if (season === 'autumn') return FT3D_PALETTE_CLASS.warm;
    if (season === 'summer' && weatherVar === 'sunny') return FT3D_PALETTE_CLASS.vivid;
    return '';
}

/** CSS class for the current backdrop palette pref (''=no filter). `weatherVar` feeds the
 *  'auto' derivation; callers without a weather signal pass '' (season/night still apply). */
export function ft3dBackdropPaletteClass(isNight: boolean, weatherVar = ''): string {
    const mode = ft3dBackdropPaletteMode();
    if (mode === 'auto') return resolveAutoPalette(isNight, weatherVar);
    return FT3D_PALETTE_CLASS[mode] ?? '';
}

// User/GM knob: window.ftSetBackdropPalette('vivid'|'warm'|'cool'|'muted'|'off'|'auto').
// Persists the pref and re-stamps any live backdrop root(s) immediately (cross-fades via CSS).
try {
    (window as unknown as { ftSetBackdropPalette?: (m: string) => void }).ftSetBackdropPalette = (mode: string): void => {
        try {
            window.localStorage?.setItem('ft_xz_bg_palette', mode);
            document.querySelectorAll<HTMLElement>('.xz-parallax-root').forEach((el) => {
                el.classList.remove(...FT3D_PALETTE_ALL_CLASSES);
                // data-xz-weather is stamped by FT3DParallaxBackground.rebuild() so the 'auto'
                // derivation keeps its weather signal on a live re-stamp.
                const cls = ft3dBackdropPaletteClass(
                    el.classList.contains('xz-parallax-night'),
                    el.dataset.ft3dWeather ?? '',
                );
                if (cls) el.classList.add(cls);
            });
        } catch { /* ignore */ }
    };
} catch { /* ignore */ }
